import React, { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { Field, Form, Formik, useFormikContext } from "formik";
import * as yup from "yup";
import { Buildings } from "@piiqtechnologies/svg-icons";
import { PeekButton, PeekDialog, PeekDialogActions, PeekDialogContent, PeekDivider, PeekStack, PeekTypography, } from "@piiqtechnologies/ui-components";
import { DownloadMode, ExportFormat } from "@constants/Export";
import { MultiSelectDropdown } from "@components/common/multi-select-dropdown";
import { OrganizationSearchSelector } from "@components/common/organization-search-selector";
import { DownloadScopeBanner } from "./DownloadScopeBanner";
import { FormatOptionCard } from "./FormatOptionCard";
import messages from "./messages.i18n";
const RequiredFieldLabel = ({ label }) => (React.createElement(PeekStack, { direction: "row", gap: 0.25, alignItems: "baseline" },
    React.createElement(PeekTypography, { variant: "h4" }, label),
    React.createElement(PeekTypography, { variant: "h4", color: "error.main" }, "*")));
const OrganizationChangeEffect = ({ onOrganizationChange, }) => {
    const { values, setFieldValue } = useFormikContext();
    const previousRef = useRef(values.organizationId);
    useEffect(() => {
        if (previousRef.current === values.organizationId)
            return;
        previousRef.current = values.organizationId;
        setFieldValue("communityIds", []);
        onOrganizationChange === null || onOrganizationChange === void 0 ? void 0 : onOrganizationChange(values.organizationId);
    }, [values.organizationId]);
    return null;
};
export const DownloadReportDialog = ({ open, onClose, mode, scopeLabel, communities = [], communityIds = [], assessmentId, onDownload, withOrganizationField = false, onOrganizationChange, communitiesLoading = false, }) => {
    const intl = useIntl();
    const isBulk = mode === DownloadMode.Bulk;
    const copy = isBulk
        ? {
            title: messages.title,
            csvDescription: messages.csvDescription,
            pdfTitle: messages.zippedPdfTitle,
            pdfDescription: messages.zippedPdfDescription,
        }
        : {
            title: messages.titleSingle,
            csvDescription: messages.csvDescriptionSingle,
            pdfTitle: messages.pdfTitleSingle,
            pdfDescription: messages.pdfDescriptionSingle,
        };
    const initialValues = useMemo(() => ({ organizationId: "", communityIds: [], format: ExportFormat.Csv }), [open]);
    const validationSchema = useMemo(() => yup.object({
        organizationId: withOrganizationField ? yup.string().required() : yup.string(),
        communityIds: isBulk ? yup.array().min(1) : yup.array(),
    }), [withOrganizationField, isBulk]);
    const handleSubmit = (values) => {
        onDownload({
            communityIds: isBulk ? values.communityIds : communityIds,
            format: values.format,
            assessmentId,
            mode,
            organizationId: withOrganizationField ? values.organizationId : undefined,
        });
        onClose();
    };
    return (React.createElement(PeekDialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, hasCloseIcon: true, title: intl.formatMessage(copy.title) },
        React.createElement(Formik, { initialValues: initialValues, validationSchema: validationSchema, enableReinitialize: true, onSubmit: handleSubmit }, ({ values, setFieldValue, isValid }) => {
            const communitiesDisabled = withOrganizationField && (!values.organizationId || communitiesLoading);
            const canDownload = isBulk ? isValid && values.communityIds.length > 0 : Boolean(assessmentId);
            return (React.createElement(Form, null,
                React.createElement(OrganizationChangeEffect, { onOrganizationChange: onOrganizationChange }),
                React.createElement(PeekDialogContent, null,
                    React.createElement(PeekStack, { gap: 2.5 },
                        React.createElement(DownloadScopeBanner, { label: scopeLabel }),
                        isBulk && withOrganizationField && (React.createElement(Field, { name: "organizationId", label: intl.formatMessage(messages.selectOrganization), placeholder: intl.formatMessage(messages.organizationPlaceholder), required: true, component: OrganizationSearchSelector })),
                        isBulk && (React.createElement(PeekStack, { gap: 1 },
                            React.createElement(RequiredFieldLabel, { label: intl.formatMessage(messages.selectCommunities) }),
                            React.createElement(MultiSelectDropdown, { options: communities, selected: values.communityIds, onChange: (ids) => setFieldValue("communityIds", ids), searchPlaceholder: intl.formatMessage(messages.searchCommunity), startIcon: React.createElement(Buildings, { width: 16, height: 16 }), disabled: communitiesDisabled, renderSummary: (count) => communitiesLoading
                                    ? intl.formatMessage(messages.communitiesLoading)
                                    : intl.formatMessage(messages.communitiesSelected, { count }) }))),
                        React.createElement(PeekDivider, { light: true }),
                        React.createElement(PeekStack, { gap: 1.5 },
                            React.createElement(PeekTypography, { variant: "h4" }, intl.formatMessage(messages.selectFormat)),
                            React.createElement(FormatOptionCard, { value: ExportFormat.Csv, title: intl.formatMessage(messages.csvTitle), description: intl.formatMessage(copy.csvDescription), selected: values.format === ExportFormat.Csv, onSelect: (format) => setFieldValue("format", format) }),
                            React.createElement(FormatOptionCard, { value: ExportFormat.ZippedPdf, title: intl.formatMessage(copy.pdfTitle), description: intl.formatMessage(copy.pdfDescription), selected: values.format === ExportFormat.ZippedPdf, onSelect: (format) => setFieldValue("format", format) })))),
                React.createElement(PeekDialogActions, { sx: { px: 3, pb: 3, gap: 1.5 } },
                    React.createElement(PeekButton, { "data-testid": "download-cancel", variant: "outlined", color: "info", size: "large", onClick: onClose }, intl.formatMessage(messages.cancel)),
                    React.createElement(PeekButton, { "data-testid": "download-confirm", type: "submit", variant: "contained", size: "large", disabled: !canDownload }, intl.formatMessage(messages.download)))));
        })));
};
export default DownloadReportDialog;
